import { globalTheme, useMixin } from '@clds/component-theme';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { AddAltDontuse } from '@clds/icon';
import { ColorPickerPopover } from './ColorPickerPopover';
import { DEFAULT_INITIAL_COLORS } from './colorPickerUtils';
import { Colors } from './components/Colors';
import { colorPickerTheme } from './theme';

const paletteItem = css`
  cursor: pointer;
`;
const PaletteWrapper = styled.ul`
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  padding: 0;
  margin: 0;
  & > li {
    ${paletteItem};
    margin: ${colorPickerTheme.palette.itemSpacing};
    position: relative;
  }
`;

const AddIcon = styled(AddAltDontuse)`
  ${paletteItem};
  margin: ${colorPickerTheme.palette.addIconSpacing};
  color: ${globalTheme.palette.contrastLow};
  display: inline-block;
  vertical-align: top;
`;

/**
 * A "grid" of predefined hex color values and ability to add custom colors
 * and select single/multiple colors from the list
 * @param onChange: (color: string[]) => void
 * @param selectedColors: string[]
 * @param size?: 'xs' | 'sm' | 'md'
 * @param colors?: string[]
 * @param onColorAdd?: (color: string[]) => void
 * @param isMulti?: boolean
 * @param isCustomizable?: boolean
 * @returns {JSX.Element}
 * @constructor
 */
export const ColorPalette = ({ onChange, selectedColors, size = 'xs', colors, onColorAdd, isMulti = false, isCustomizable = true }) => {
  const [currentCustomColor, setCurrentCustomColor] = useState('#ffffff');
  const addIconSize = useMixin(colorPickerTheme.field.sizeVariant[size].addIcon);
  const predefinedColors = useMemo(() => (Array.isArray(colors) && colors.length ? Array.from(new Set(colors)) : DEFAULT_INITIAL_COLORS), [colors]);

  /**
   * toggle selected arr by adding or removing
   * if the color already exists - toggle it by filtering
   * * @param color: hex string | event
   */
  const handlePaletteChange = useCallback(
    (eventOrValue) => {
      const receivedColor = eventOrValue?.currentTarget?.getAttribute('value') || eventOrValue;
      const uniqueSelected = selectedColors?.filter((item) => item !== receivedColor);
      onChange?.(selectedColors?.includes(receivedColor) ? uniqueSelected : !isMulti ? [receivedColor] : [...uniqueSelected, receivedColor]);
    },
    [isMulti, onChange, selectedColors]
  );
  const handleAddColor = useCallback(
    (hex) => {
      !colors.includes(hex) && onColorAdd?.(hex);
      handlePaletteChange(hex);
    },
    [colors, handlePaletteChange, onColorAdd]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        handlePaletteChange(event);
      }
    },
    [handlePaletteChange]
  );

  return (
    <>
      {
        <PaletteWrapper data-test="color-palette">
          <Colors {...{ predefinedColors, handlePaletteChange, handleKeyDown, selectedColors, size }} />
          {isCustomizable && (
            <ColorPickerPopover
              data-test-specifier={currentCustomColor}
              color={currentCustomColor}
              onChange={setCurrentCustomColor}
              onChangeComplete={handleAddColor}
              triggeredElement={<AddIcon size={addIconSize} />}
            />
          )}
        </PaletteWrapper>
      }
    </>
  );
};

ColorPalette.propTypes = {
  onChange: PropTypes.func.isRequired,
  selectedColors: PropTypes.array,
  size: PropTypes.oneOf(['xs', 'sm', 'md']),
  colors: PropTypes.arrayOf(PropTypes.string),
  onColorAdd: PropTypes.func,
  isMulti: PropTypes.bool,
  isCustomizable: PropTypes.bool,
};
